import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, QrCode, ShieldCheck, MessageCircleReply, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const HERO = "https://images.unsplash.com/photo-1600147131759-880e94a6185f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const QR_FLOW = ["Scannez le QR code au guichet", "Notez et décrivez en 1 minute", "L'organisation vous répond et s'améliore"];

const STEPS = [
  { icon: QrCode, title: "Scannez ou recherchez", text: "Un QR code en agence, au guichet ou sur un reçu ouvre directement le bon formulaire." },
  { icon: MessageCircleReply, title: "Exprimez-vous", text: "Avis, suggestion ou réclamation — avec ou sans compte, en toute confidentialité si vous le souhaitez." },
  { icon: ShieldCheck, title: "Suivez la réponse", text: "Un code de suivi unique vous permet de consulter le traitement et d'échanger avec l'organisation." },
];

const PILLARS = [
  { title: "Multi-entreprises", text: "Chaque organisation dispose d'un espace isolé : agences, services, équipes et données qui lui sont propres." },
  { title: "Améliorer les services", text: "Problèmes récurrents, agences en difficulté et délais de traitement : chaque organisation sait quoi corriger en priorité." },
  { title: "Analyse par IA", text: "Sentiment, catégorie, synthèse et proposition de réponse générés pour chaque retour reçu." },
  { title: "Public & privé", text: "Pensé pour les administrations comme pour les entreprises, dans plusieurs pays." },
];

const QuickTrack = () => {
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) { navigate(`/suivi?code=${encodeURIComponent(code.trim())}`); } }}
      className="flex w-full max-w-md gap-2" data-testid="hero-track-form">
      <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code de suivi (ex : FB-XXXXXXXX)"
        className="h-11 rounded-full bg-white font-mono" data-testid="hero-track-input" />
      <Button type="submit" variant="outline" className="h-11 rounded-full" data-testid="hero-track-submit">Suivre</Button>
    </form>
  );
};

export default function Landing() {
  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pt-24">
          <div className="lg:col-span-7">
            <p className="eyebrow rise">Plateforme de la voix des usagers</p>
            <h1 className="rise rise-1 mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Chaque avis compte.<br /><span className="text-blue-600">Chaque réclamation</span> mérite une réponse.
            </h1>
            <p className="rise rise-2 mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              VOXA AFRIKA collecte, analyse et traite les avis, suggestions et réclamations des usagers de services publics et privés — de l'agence de quartier au réseau national.
            </p>
            <div className="rise rise-3 mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full" data-testid="hero-give-feedback-button">
                <Link to="/entreprises"><Search className="mr-2 h-4 w-4" />Déposer un avis</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="hero-company-button">
                <Link to="/inscription-entreprise">Créer l'espace de mon organisation <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="rise rise-3 mt-10">
              <p className="mb-2 text-sm font-medium text-slate-700">Vous avez déjà déposé un dossier ?</p>
              <QuickTrack />
            </div>
          </div>
          <div className="relative lg:col-span-5">
            <div className="grain relative h-full min-h-[340px] overflow-hidden rounded-3xl">
              <img src={HERO} alt="Usager scannant un QR code au comptoir d'une agence" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/20 bg-slate-900/40 p-4 text-white backdrop-blur-xl" data-testid="hero-qr-flow">
                <p className="flex items-center gap-2 text-sm font-semibold"><QrCode className="h-4 w-4" /> Sans compte, depuis votre téléphone</p>
                <ol className="mt-2 space-y-1 text-sm text-white/90">{QR_FLOW.map((s, i) => <li key={s} className="flex gap-2"><span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-indigo-400 text-xs font-bold text-indigo-950">{i + 1}</span>{s}</li>)}</ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <p className="eyebrow">Comment ça marche</p>
          <h2 className="mt-3 max-w-xl font-display text-2xl font-bold tracking-tight sm:text-3xl">Trois étapes, aucune inscription obligatoire.</h2>
          <Link to="/comment-ca-marche" className="mt-3 inline-block text-sm font-medium text-blue-600" data-testid="landing-how-link">En savoir plus sur la collecte par QR code →</Link>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative">
                <span className="font-mono text-xs text-slate-400">0{i + 1}</span>
                <s.icon className="mt-3 h-6 w-6 text-blue-600" />
                <h3 className="mt-4 font-display text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Pour les organisations</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">Un espace dédié pour écouter, répondre et s'améliorer.</h2>
            <Button asChild className="mt-6 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="pillars-signup-button">
              <Link to="/inscription-entreprise">Demander l'ouverture d'un espace</Link>
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-3 lg:col-span-8">
            {PILLARS.map((p) => (
              <div key={p.title} className="card-surface p-6 transition-transform duration-300 hover:-translate-y-1">
                <h3 className="font-display font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
